import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import Icon, { QuestionCircleOutlined } from '@ant-design/icons';
import {
  achLogo,
  checkLogo,
  flexLogo,
  plaidLogo,
  QBCashLogo,
  rppsLogo,
  shopifyWalletLogo
} from '@tools/ui/assets';
import { CardAccount, Maybe } from '@tools/ui/types/generated/graphql';
import {
  buildIconAndNameFromNetwork,
  extractNetworkFromCard
} from '@tools/ui-utils/card.util';
import { PaymentMethodType } from '@tools/ui-utils/payment-method.util';
import { theme } from '@tools/ui-utils/theme.util';
import TableIcon from '../common/icons/tableIcon.icon';

export const fundingSourceExtraFields = [
  'fundingSource.isVerified',
  'fundingSource.plaidAccountId',
  'fundingSource.bankAccountId',
  'fundingSource.intuitAccountId',
  'fundingSource.origin',
  'fundingSource.cardAccount.cardType',
  'fundingSource.cardAccount.network'
];

export enum PaymentMethodUsageType {
  paymentMethod,
  deliveryMethod
}

type IProps = {
  cardAccount?: Maybe<CardAccount>;
  isVerified?: boolean;
  paymentMethodType: PaymentMethodType;
  source?: PaymentMethodUsageType;
  fontSize?: number;
  'data-testid'?: string;
};

const PaymentMethodIcon: FC<React.PropsWithChildren<IProps>> = ({
  cardAccount,
  isVerified,
  paymentMethodType,
  source = PaymentMethodUsageType.paymentMethod,
  'data-testid': dataTestId
}) => {
  const { t } = useTranslation();
  let color;
  let tooltip;
  let icon = QuestionCircleOutlined;

  if (paymentMethodType === PaymentMethodType.PLAID) {
    icon = plaidLogo as typeof Icon;
    tooltip = 'Plaid';

    if (source === PaymentMethodUsageType.paymentMethod) {
      color = isVerified
        ? theme.colors.isVerified.yes
        : theme.colors.isVerified.no;
    }
  } else if (paymentMethodType === PaymentMethodType.BANK) {
    icon = achLogo as typeof Icon;
    tooltip = 'ACH';
    if (source === PaymentMethodUsageType.paymentMethod) {
      tooltip = `ACH - MD ${isVerified ? t('verified') : t('unverified')}`;
      color = isVerified
        ? theme.colors.isVerified.yes
        : theme.colors.isVerified.no;
    }
  } else if (
    paymentMethodType === PaymentMethodType.CREDIT_CARD ||
    paymentMethodType === PaymentMethodType.DEBIT_CARD
  ) {
    const network = extractNetworkFromCard(cardAccount);
    const result = buildIconAndNameFromNetwork(
      network,
      tooltip,
      cardAccount?.cardType
    );
    icon = result.icon;
    tooltip = result.tooltip;
  } else if (paymentMethodType === PaymentMethodType.QB_CASH) {
    icon = QBCashLogo as typeof Icon;
    tooltip = 'QB cash';
  } else if (paymentMethodType === PaymentMethodType.CHECK) {
    icon = checkLogo as typeof Icon;
    tooltip = 'Check';
  } else if (paymentMethodType === PaymentMethodType.RPPS) {
    icon = rppsLogo as typeof Icon;
    tooltip = 'RPPS (Biller directory)';
  } else if (paymentMethodType === PaymentMethodType.SHOPIFY) {
    icon = shopifyWalletLogo as typeof Icon;
    tooltip = 'Shopify Balance';
  } else if (paymentMethodType === PaymentMethodType.FLEX) {
    icon = flexLogo as typeof Icon;
    tooltip = 'Flex';
  }
  return (
    <TableIcon
      component={icon}
      tooltip={tooltip}
      style={{ color }}
      data-testid={dataTestId}
    />
  );
};

export default PaymentMethodIcon;
